import { ref, watch } from 'vue'

/**
 * Composable for managing theme state
 * @returns An object containing theme state and functions to manipulate it
 */
export function useTheme(): {
  isDark: typeof isDark
  toggleTheme: () => void
  setTheme: (dark: boolean) => void
} {
  const isDark = ref(false)

  // Initialize theme based on localStorage or system preference
  const initTheme = () => {
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      isDark.value = savedTheme === 'dark'
    } else {
      // Check system preference
      isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    updateThemeClass()
  }

  // Update the HTML class
  const updateThemeClass = () => {
    if (isDark.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Toggle theme
  const toggleTheme = () => {
    isDark.value = !isDark.value
  }

  // Set theme explicitly
  const setTheme = (dark: boolean) => {
    isDark.value = dark
  }

  // Watch for theme changes
  watch(isDark, (newValue) => {
    localStorage.setItem('theme', newValue ? 'dark' : 'light')
    updateThemeClass()
  })

  // Initialize on creation
  initTheme()

  return {
    isDark,
    toggleTheme,
    setTheme
  }
}
